import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home',     path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Services', path: '/services' },
    { name: 'About',    path: '/about' },
    { name: 'Contact',  path: '/contact' },
  ];

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">

          {/* Logo — /logo.png in public folder */}
          <Link to="/" className="flex items-center group">
            <motion.img
              whileHover={{ scale: 1.04 }}
              src="/logo.png"
              alt="GTG IT Solutions & Services"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`relative font-medium transition-colors ${
                  location.pathname === link.path
                    ? scrolled ? 'text-gtg-cyan-500' : 'text-white'
                    : scrolled ? 'text-gray-700 hover:text-gtg-cyan-500' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div layoutId="navbar-indicator"
                    className={`absolute -bottom-1 left-0 right-0 h-0.5 ${scrolled ? 'bg-gtg-cyan-500' : 'bg-white'}`}
                  />
                )}
              </Link>
            ))}

            {user ? (
              <div className="flex items-center space-x-3">
                {isAdmin && (
                  <Link to="/admin"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      scrolled ? 'bg-gtg-blue-500 text-white hover:bg-gtg-blue-600' : 'bg-white/20 text-white hover:bg-white/30'
                    }`}>Admin</Link>
                )}
                <button onClick={handleLogout}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    scrolled ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-white/20 text-white hover:bg-white/30'
                  }`}>Logout</button>
              </div>
            ) : (
              <Link to="/admin/login"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  scrolled ? 'bg-gtg-cyan-500 text-white hover:bg-gtg-cyan-600' : 'bg-white/20 text-white hover:bg-white/30'
                }`}>
                <UserCircleIcon className="w-5 h-5" /><span>Login</span>
              </Link>
            )}
          </div>

          <button onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/20'
            }`}>
            {isOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden mt-4">
              <div className="bg-white rounded-lg shadow-xl p-4 space-y-3">
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}>{link.name}</Link>
                ))}
                {user ? (
                  <>
                    {isAdmin && <Link to="/admin" onClick={() => setIsOpen(false)} className="block px-4 py-3 rounded-lg font-medium bg-gtg-blue-500 text-white text-center">Admin Dashboard</Link>}
                    <button onClick={() => { handleLogout(); setIsOpen(false); }} className="w-full px-4 py-3 rounded-lg font-medium bg-gray-200 text-gray-700">Logout</button>
                  </>
                ) : (
                  <Link to="/admin/login" onClick={() => setIsOpen(false)} className="block px-4 py-3 rounded-lg font-medium bg-gradient-to-r from-gtg-cyan-500 to-gtg-green-500 text-white text-center">Login</Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;